import React, { useState, useEffect } from 'react';
import { Box, Typography, ToggleButtonGroup, ToggleButton, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../../api/axiosConfig';

/**
 * Zone definitions for the heatmap overlay.
 * Each zone has a position on the SVG court and will display FG%.
 */
const ZONE_POSITIONS = {
  'paint':            { x: 250, y: 400, w: 150, h: 100 },
  'mid-left':         { x: 80,  y: 350, w: 80,  h: 70 },
  'mid-right':        { x: 420, y: 350, w: 80,  h: 70 },
  'mid-top':          { x: 250, y: 290, w: 140, h: 60 },
  'mid-range':        { x: 250, y: 330, w: 100, h: 50 },
  '3pt-left-corner':  { x: 40,  y: 420, w: 60,  h: 50 },
  '3pt-right-corner': { x: 460, y: 420, w: 60,  h: 50 },
  '3pt-left-wing':    { x: 70,  y: 260, w: 70,  h: 60 },
  '3pt-right-wing':   { x: 430, y: 260, w: 70,  h: 60 },
  '3pt-top':          { x: 250, y: 60,  w: 160, h: 60 },
};

function getHeatColor(fgPct) {
  if (fgPct === null || fgPct === undefined) return 'rgba(128,128,128,0.3)';
  if (fgPct >= 60) return 'rgba(76, 175, 80, 0.6)';   // green - hot
  if (fgPct >= 45) return 'rgba(139, 195, 74, 0.5)';   // light green
  if (fgPct >= 35) return 'rgba(255, 235, 59, 0.5)';   // yellow
  if (fgPct >= 25) return 'rgba(255, 152, 0, 0.5)';    // orange
  return 'rgba(244, 67, 54, 0.5)';                      // red - cold
}

/**
 * Shot chart visualization component.
 * Shows individual shots on court OR zone-based heatmap.
 *
 * Props:
 *   - gameId: number (optional, for single game view)
 *   - playerId: number (for aggregate view)
 *   - mode: 'dots' | 'heatmap' (default: 'dots')
 */
export default function ShotChartVisualization({ gameId, playerId }) {
  const { t } = useTranslation();
  const [shots, setShots] = useState([]);
  const [zoneStats, setZoneStats] = useState([]);
  const [viewMode, setViewMode] = useState('dots');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (gameId) {
          // Single game - fetch individual shots
          const params = playerId ? `?playerId=${playerId}` : '';
          const res = await api.get(`/api/stats/shot-chart/${gameId}${params}`);
          setShots(res.data || []);
        }

        if (playerId && !gameId) {
          // Aggregate - fetch zone stats
          const res = await api.get(`/api/stats/shot-chart/player/${playerId}`);
          setZoneStats(res.data || []);
          setViewMode('heatmap');
        }
      } catch (err) {
        console.error('Error fetching shot chart data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (gameId || playerId) fetchData();
  }, [gameId, playerId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={30} />
      </Box>
    );
  }

  const hasData = shots.length > 0 || zoneStats.length > 0;

  if (!hasData) {
    return (
      <Box sx={{ textAlign: 'center', py: 4, color: 'grey.500' }}>
        <Typography variant="body2">{t('shotChart.noData', { defaultValue: 'No shot chart data available' })}</Typography>
      </Box>
    );
  }

  // Compute zone stats from individual shots (for single game)
  const computedZoneStats = {};
  if (viewMode === 'heatmap' && shots.length > 0) {
    shots.forEach(s => {
      const zone = s.zone || 'unknown';
      if (!computedZoneStats[zone]) computedZoneStats[zone] = { total: 0, made: 0 };
      computedZoneStats[zone].total += 1;
      computedZoneStats[zone].made += s.made;
    });
  }

  // Use API zone stats for aggregate, or computed for single game
  const zoneData = {};
  if (zoneStats.length > 0) {
    zoneStats.forEach(z => {
      zoneData[z.zone] = { total: z.total_shots, made: z.made_shots, pct: z.fg_pct };
    });
  } else {
    Object.entries(computedZoneStats).forEach(([zone, data]) => {
      zoneData[zone] = { ...data, pct: data.total > 0 ? Math.round(data.made / data.total * 1000) / 10 : 0 };
    });
  }

  // Shot totals
  const totalShots = shots.length || zoneStats.reduce((sum, z) => sum + z.total_shots, 0);
  const totalMade = shots.filter(s => s.made).length || zoneStats.reduce((sum, z) => sum + z.made_shots, 0);
  const overallPct = totalShots > 0 ? Math.round(totalMade / totalShots * 1000) / 10 : 0;

  return (
    <Box>
      {/* Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {totalMade}/{totalShots} ({overallPct}%)
        </Typography>
        {shots.length > 0 && (
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, val) => val && setViewMode(val)}
            size="small"
          >
            <ToggleButton value="dots" sx={{ fontSize: '11px', py: 0.3, px: 1 }}>
              {t('shotChart.dots', { defaultValue: 'Shots' })}
            </ToggleButton>
            <ToggleButton value="heatmap" sx={{ fontSize: '11px', py: 0.3, px: 1 }}>
              {t('shotChart.heatmap', { defaultValue: 'Zones' })}
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      {/* Court SVG */}
      <svg viewBox="0 0 500 470" style={{ width: '100%', borderRadius: 8, backgroundColor: '#2d5a27' }}>
        {/* Court lines */}
        <rect x="10" y="10" width="480" height="450" fill="none" stroke="white" strokeWidth="2" />
        <path d="M 75 460 L 75 310 A 170 170 0 0 1 425 310 L 425 460" fill="none" stroke="white" strokeWidth="2" />
        <rect x="155" y="310" width="190" height="150" fill="none" stroke="white" strokeWidth="2" />
        <path d="M 190 310 A 60 60 0 0 1 310 310" fill="none" stroke="white" strokeWidth="2" />
        <circle cx="250" cy="310" r="60" fill="none" stroke="white" strokeWidth="2" strokeDasharray="8 8" />
        <path d="M 220 460 A 30 30 0 0 1 280 460" fill="none" stroke="white" strokeWidth="2" />
        <circle cx="250" cy="445" r="8" fill="none" stroke="#ff6600" strokeWidth="2.5" />
        <line x1="220" y1="435" x2="280" y2="435" stroke="#ff6600" strokeWidth="3" />
        <line x1="10" y1="10" x2="490" y2="10" stroke="white" strokeWidth="2" />

        {/* Heatmap zones */}
        {viewMode === 'heatmap' && Object.entries(ZONE_POSITIONS).map(([zone, pos]) => {
          const data = zoneData[zone];
          if (!data) return null;
          return (
            <g key={zone}>
              <rect
                x={pos.x - pos.w / 2}
                y={pos.y - pos.h / 2}
                width={pos.w}
                height={pos.h}
                rx="8"
                fill={getHeatColor(data.pct)}
              />
              <text
                x={pos.x}
                y={pos.y - 5}
                textAnchor="middle"
                fill="white"
                fontSize="14"
                fontWeight="bold"
              >
                {data.pct}%
              </text>
              <text
                x={pos.x}
                y={pos.y + 12}
                textAnchor="middle"
                fill="rgba(255,255,255,0.7)"
                fontSize="10"
              >
                {data.made}/{data.total}
              </text>
            </g>
          );
        })}

        {/* Individual shots */}
        {viewMode === 'dots' && shots.map((shot, i) => (
          <g key={i}>
            {shot.made ? (
              <circle
                cx={shot.court_x * 5}
                cy={shot.court_y * 4.7}
                r="5"
                fill="#4caf50"
                fillOpacity="0.8"
                stroke="white"
                strokeWidth="0.5"
              />
            ) : (
              <g transform={`translate(${shot.court_x * 5}, ${shot.court_y * 4.7})`}>
                <line x1="-4" y1="-4" x2="4" y2="4" stroke="#f44336" strokeWidth="2" />
                <line x1="4" y1="-4" x2="-4" y2="4" stroke="#f44336" strokeWidth="2" />
              </g>
            )}
          </g>
        ))}
      </svg>

      {/* Legend */}
      {viewMode === 'heatmap' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
          {[
            { label: '60%+', color: 'rgba(76, 175, 80, 0.8)' },
            { label: '45-60%', color: 'rgba(139, 195, 74, 0.7)' },
            { label: '35-45%', color: 'rgba(255, 235, 59, 0.7)' },
            { label: '25-35%', color: 'rgba(255, 152, 0, 0.7)' },
            { label: '<25%', color: 'rgba(244, 67, 54, 0.7)' },
          ].map(item => (
            <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '2px', bgcolor: item.color }} />
              <Typography variant="caption" sx={{ fontSize: '10px' }}>{item.label}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
